// How hard the mailbox makes you think before mail leaves it (busy-my-web#516).
//
// A Pro upgrade synced ~21k threads for one user, and ~7,195 of their email timelines were
// permanently erased the same day — through confirms that either named no count at all ("Delete
// all conversation forever?") or said "erase or archive" without the word that mattered. So every
// destructive path now asks through these rules, and they live here, as plain functions, so the
// threshold and the wording can be checked without mounting a single dialog:
//
//   • moving MANY conversations to Trash (soft, restorable) states the exact count first;
//   • a PERMANENT delete of any size states the count AND the word "permanently";
//   • a permanent delete above PERMANENT_TYPED_OVER also asks for the count to be typed back —
//     a second, deliberate gesture nobody makes by reflex.

/** Above this many conversations, a move to Trash asks first, naming the count. */
export const BULK_BIN_CONFIRM_OVER = 50

/** Above this many conversations, a permanent delete needs the count typed back to go ahead. */
export const PERMANENT_TYPED_OVER = 500

const toCount = (n) => {
  const v = Math.floor(Number(n))
  return Number.isFinite(v) && v > 0 ? v : 0
}

/** "1,234" — the count as the user reads it and as they may type it back. */
export const formatCount = (n) => toCount(n).toLocaleString('en-GB')

const conversations = (n) => `${formatCount(n)} conversation${toCount(n) === 1 ? '' : 's'}`

/** Whether moving `count` conversations to Trash has to be confirmed first. */
export const needsBinConfirm = (count) => toCount(count) > BULK_BIN_CONFIRM_OVER

/** Whether a permanent delete of `count` conversations needs the count typed back. */
export const needsTypedCount = (count) => toCount(count) > PERMANENT_TYPED_OVER

/** The question a big move to Trash asks — the count, where it goes, and that it can come back. */
export const binConfirmMessage = (count) =>
  `Move ${conversations(count)} to Trash? You can restore them from Trash.`

/** The question every permanent delete asks — the count and "permanently", whatever the size. */
export const permanentConfirmMessage = (count) =>
  `Permanently delete ${conversations(count)}? This cannot be undone.`

/** The same fact as a statement, for a confirm whose question is already drawn (the folder card). */
export const permanentCountLine = (count) =>
  `${conversations(count)} will be permanently deleted. This cannot be undone.`

/** The dialog title over a permanent delete. */
export const permanentConfirmTitle = (count) =>
  toCount(count) === 1 ? 'Delete forever' : `Delete ${conversations(count)} forever`

/**
 * What has to be typed for a permanent delete to go ahead: the count itself, or '' when no typing
 * is needed. The count rather than a word like DELETE because the count is the fact being agreed
 * to — typing "7195" is reading "7195".
 */
export const typedCountPhrase = (count) => (needsTypedCount(count) ? String(toCount(count)) : '')

/** The line under the field. */
export const typedCountPrompt = (phrase) => (phrase ? `Type ${phrase} to confirm.` : '')

/**
 * Whether what was typed matches the phrase. Separators and surrounding spaces are forgiven
 * ("7,195", " 7195 ") because the count is DISPLAYED with them; anything else is not.
 * No phrase means nothing has to be typed, which always matches.
 */
export const typedCountMatches = (typed, phrase) => {
  if (!phrase) return true
  const clean = String(typed ?? '').replace(/[\s,.]/g, '')
  return clean === String(phrase)
}

/**
 * The confirm the Work-board raises before a big "Delete conversations" (busy-my-web#516) — the
 * same `deleteConfirm` shape the group delete's strip and action row already draw, tagged with
 * `kind: 'threads'` so the strip's Delete knows to send the thread ids and not sender groups.
 * Null when the selection is small enough to go straight to Trash.
 */
export function threadsBinConfirmation(ids = [], { confirm = 'Delete', cancel = 'Cancel' } = {}) {
  const list = (ids || []).filter(Boolean)
  if (!needsBinConfirm(list.length)) return null
  return {
    kind: 'threads',
    ids: list,
    senders: [],
    emails: 0,
    groups: 0,
    text: binConfirmMessage(list.length),
    confirm,
    cancel,
  }
}
